import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and client view mode to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ofimex_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Check if client view mode is active (set by AuthContext)
  const clientViewMode = localStorage.getItem('ofimex_client_view_mode') === 'true';
  if (clientViewMode) {
    config.headers['X-Client-View-Mode'] = 'true';
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ofimex_token');
      localStorage.removeItem('ofimex_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const registerUser = (data) => api.post('/auth/register', data);
export const getUsers = () => api.get('/users');
export const toggleUserActive = (userId) => api.put(`/users/${userId}/toggle-active`);
export const toggleMasterAdmin = (userId) => api.put(`/users/${userId}/toggle-master-admin`);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);
export const deleteUsersBulk = (userIds) => api.delete('/users/bulk', { data: userIds });
export const bulkUpdateUsers = (userIds, data) => {
  const params = new URLSearchParams();
  userIds.forEach(id => params.append('user_ids', id));
  if (data.role) params.append('role', data.role);
  if (data.is_active !== undefined) params.append('is_active', data.is_active);
  return api.put(`/users/bulk-update?${params.toString()}`);
};
export const getPermissionsCatalog = () => api.get('/permissions/catalog');
export const getUserPermissions = (userId) => api.get(`/users/${userId}/permissions`);
export const updateUserPermissions = (userId, data) => api.put(`/users/${userId}/permissions`, data);
export const getUserPermissionsAudit = (userId) => api.get(`/users/${userId}/permissions/audit`);
export const syncShipmentNow = (mbl) => api.post(`/tracking/sync-now/${encodeURIComponent(mbl)}`);
export const revalidateContainer = (containerNumber) => api.post(`/tracking/revalidate/${encodeURIComponent(containerNumber)}`);
export const revalidateShipment = (mbl) => api.post(`/tracking/revalidate-mbl/${encodeURIComponent(mbl)}`);
export const repairTrackingNow = () => api.post('/tracking/repair-now');
export const getSyncHealth = (params = {}) => api.get('/saas-dashboard/sync-health', { params });
export const requestPasswordReset = (email) => api.post('/auth/request-password-reset', { email });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token/${token}`);
export const resetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password });

// Catalogs
export const getCatalog = (type, includeInactive = false) => 
  api.get(`/catalogs/${type}`, { params: { include_inactive: includeInactive } });
export const createCatalogItem = (type, data) => api.post(`/catalogs/${type}`, data);
export const deleteCatalogItem = (type, code) => api.delete(`/catalogs/${type}/${encodeURIComponent(code)}`);
export const updateCatalogItem = (type, code, data) => api.put(`/catalogs/${type}/${code}`, data);

// Clients
export const getClients = () => api.get('/clients');
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const toggleClientActive = (id) => api.put(`/clients/${id}/toggle-active`);

// Importers
export const getImporters = () => api.get('/importers');
export const createImporter = (data) => api.post('/importers', data);
export const updateImporter = (id, data) => api.put(`/importers/${id}`, data);
export const toggleImporterActive = (id) => api.put(`/importers/${id}/toggle-active`);
export const uploadImporterLogo = (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/importers/${id}/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const uploadImporterSignature = (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/importers/${id}/upload-signature`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };

// Suppliers
export const getSuppliers = () => api.get('/suppliers');
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const toggleSupplierActive = (id) => api.put(`/suppliers/${id}/toggle-active`);

// Shipments
export const getShipments = (params = {}) => api.get('/shipments', { params });
export const getShipment = (mbl) => api.get(`/shipments/${encodeURIComponent(mbl)}`);
export const createShipment = (data) => api.post('/shipments', data);
export const updateShipment = (mbl, data) => api.put(`/shipments/${encodeURIComponent(mbl)}`, data);
export const deleteShipment = (mbl) => api.delete(`/shipments/${encodeURIComponent(mbl)}`);
export const changeShipmentMbl = (oldMbl, newMbl) => api.put(`/shipments/${encodeURIComponent(oldMbl)}/change-mbl?new_mbl=${encodeURIComponent(newMbl)}`);
export const bulkUpdateShipments = (mbls, data) => {
  const params = new URLSearchParams();
  mbls.forEach(mbl => params.append('mbls', mbl));
  Object.entries(data).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  return api.put(`/shipments/bulk-update?${params.toString()}`);
};
export const uploadCostBreakdown = (mbl, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/shipments/${mbl}/upload-cost-breakdown`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Excel Import/Export
export const exportShipmentsTemplate = (params = {}) => api.get('/export/shipments-template', { 
  params, 
  responseType: 'blob' 
});
export const importShipmentsExcel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/export/shipments-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Containers
export const getContainers = (mbl) => api.get(`/shipments/${mbl}/containers`);
export const createContainer = (mbl, data) => api.post(`/shipments/${mbl}/containers`, data);
export const createContainersBulk = (mbl, containers) => 
  api.post(`/shipments/${mbl}/containers/bulk`, { containers });
export const updateContainer = (mbl, containerId, data) => 
  api.put(`/shipments/${mbl}/containers/${containerId}`, data);
export const bulkUpdateContainers = (mbl, data) => 
  api.put(`/shipments/${mbl}/containers/bulk-update`, data);
export const deleteContainer = (mbl, containerId) => 
  api.delete(`/shipments/${mbl}/containers/${containerId}`);
export const getDeliveredContainers = (params = {}) => api.get('/containers/delivered', { params });
export const cleanupContainerMbls = () => api.post('/containers/cleanup-mbls');
export const findDuplicateShipments = () => api.get('/containers/find-duplicates');
export const removeDuplicateShipments = () => api.post('/containers/remove-duplicates');

// Events
export const getEvents = (mbl, containerNumber = null) => 
  api.get(`/shipments/${mbl}/events`, { params: { container_number: containerNumber } });
export const createEvent = (mbl, data) => api.post(`/shipments/${mbl}/events`, data);
export const deleteEvent = (mbl, eventId) => api.delete(`/shipments/${mbl}/events/${eventId}`);

// Internal Notes
export const getNotes = (mbl, containerNumber = null) => 
  api.get(`/shipments/${mbl}/notes`, { params: { container_number: containerNumber } });
export const createNote = (mbl, data) => api.post(`/shipments/${mbl}/notes`, data);
export const deleteNote = (mbl, noteId) => api.delete(`/shipments/${mbl}/notes/${noteId}`);

// Dashboard
export const getDashboardMetrics = (params = {}) => api.get('/dashboard/metrics', { params });
export const getContainerEvolution = (params = {}) => api.get('/dashboard/container-evolution', { params });
export const getAvailableWidgets = () => api.get('/dashboard/widgets');
export const getDashboardPreferences = () => api.get('/dashboard/preferences');
export const updateDashboardPreferences = (prefs) => api.put('/dashboard/preferences', prefs);
export const getWidgetData = (widgetId, params = {}) => api.get(`/dashboard/widget-data/${widgetId}`, { params });
export const getPeriodComparison = (period1, period2) => api.get('/dashboard/period-comparison', { params: { period1, period2 } });

// Client Dashboard
export const getClientDashboard = () => api.get('/client/dashboard');

// Export
export const exportShipments = (params = {}) => {
  return api.get('/export/shipments', { 
    params, 
    responseType: 'blob' 
  });
};
export const exportShipmentDetail = (mbl) => {
  return api.get(`/export/shipments/${mbl}`, { 
    responseType: 'blob' 
  });
};
export const exportCostsReport = (params = {}) => {
  return api.get('/export/costs-report', { 
    params, 
    responseType: 'blob' 
  });
};

// Seed
export const seedData = () => api.post('/seed');

// Notion Integration
export const getNotionStatus = () => api.get('/notion/status');
export const importFromNotion = (overwrite = false) => api.post('/notion/import', { overwrite });
export const exportToNotion = (mbl) => api.post(`/notion/export/${mbl}`);
export const exportAllToNotion = () => api.post('/notion/export-all');

// Feature Flags & Plans
export const getFeatures = () => api.get('/features');
export const getPlans = () => api.get('/plans');
export const getPublicPlans = () => api.get('/public/plans');  // For landing/registration pages
export const createPlan = (data) => api.post('/plans', data);
export const updatePlan = (code, data) => api.put(`/plans/${code}`, data);
export const deletePlan = (code) => api.delete(`/plans/${code}`);

// Organizations
export const getOrganizations = () => api.get('/organizations');
export const createOrganization = (data) => api.post('/organizations', data);
export const updateOrganization = (id, data) => api.put(`/organizations/${id}`, data);
export const toggleOrgFeature = (orgId, featureCode, enabled) => 
  api.put(`/organizations/${orgId}/features/${featureCode}?enabled=${enabled}`);
export const approveOrganization = (orgId) => api.put(`/organizations/${orgId}/approve`);
export const rejectOrganization = (orgId) => api.put(`/organizations/${orgId}/reject`);
export const deactivateOrganization = (orgId, reason, notes = null) => 
  api.put(`/organizations/${orgId}/deactivate`, { reason, notes });
export const reactivateOrganization = (orgId) => api.put(`/organizations/${orgId}/reactivate`);

// Cost Breakdown
export const getShipmentCosts = (mbl) => api.get(`/shipments/${mbl}/costs`);
export const createCostBreakdown = (mbl, data) => api.post(`/shipments/${mbl}/costs`, data);
export const updateCostBreakdown = (mbl, costId, data) => api.put(`/shipments/${mbl}/costs/${costId}`, data);
export const deleteCostBreakdown = (mbl, costId) => api.delete(`/shipments/${mbl}/costs/${costId}`);
export const normalizeAmounts = () => api.post('/costs/admin/normalize-amounts');

// Audit
export const getShipmentAudit = (mbl) => api.get(`/shipments/${mbl}/audit`);
export const getRecentAudit = (limit = 100) => api.get(`/audit/recent?limit=${limit}`);

// Contact Leads (Master Admin)
export const getContactLeads = () => api.get('/contact-leads');
export const deleteContactLead = (leadId) => api.delete(`/contact-leads/${leadId}`);
export const updateLeadStatus = (leadId, status) => api.put(`/contact-leads/${leadId}/status?status=${status}`);

// Payments - Balance de Pagos
export const getPayments = (params = {}) => api.get('/payments', { params });
export const createPayment = (data) => api.post('/payments', data);
export const updatePayment = (paymentId, data) => api.put(`/payments/${paymentId}`, data);
export const deletePayment = (paymentId) => api.delete(`/payments/${paymentId}`);
export const getPaymentMethods = () => api.get('/payments/methods');
export const getClientBalance = (clientId) => api.get(`/payments/balance/${clientId}`);
export const getAllBalances = () => api.get('/payments/balances');
export const setInitialBalance = (clientId, amount, asOfDate, notes = '') => 
  api.post(`/payments/initial-balance/${clientId}?amount=${amount}&as_of_date=${asOfDate}&notes=${encodeURIComponent(notes)}`);

// Chatbot
export const sendChatMessage = (message, sessionId = null) => 
  api.post('/chatbot/chat', { message, session_id: sessionId });
export const getChatHistory = (sessionId = null, limit = 20) => 
  api.get('/chatbot/history', { params: { session_id: sessionId, limit } });
export const clearChatHistory = (sessionId) => api.delete(`/chatbot/history/${sessionId}`);

// Analytics - ETA Prediction
export const getEtaStatistics = () => api.get('/analytics/eta-stats');
export const calculateAllEtas = () => api.post('/analytics/calculate-etas');
export const getShipmentEtaPrediction = (mbl) => api.get(`/analytics/shipment-eta/${encodeURIComponent(mbl)}`);
export const getDeliveryTimesReport = () => api.get('/analytics/delivery-times-report');
export const getDeliveryTimesDetailed = (params = {}) => api.get('/analytics/delivery-times-detailed', { params });
export const getSlaDashboard = () => api.get('/analytics/sla-dashboard');


// Arnulfo (Master Admin only)
export const getArnulfoDashboard = () => api.get('/arnulfo/dashboard');
export const updateArnulfoStatus = (mbl, status) => api.put(`/arnulfo/shipment/${encodeURIComponent(mbl)}/status?status=${status}`);
export const toggleArnulfo = (mbl) => api.put(`/arnulfo/shipment/${encodeURIComponent(mbl)}/toggle`);
export const updateArnulfoContainer = (containerNumber, data) => api.put(`/arnulfo/container/${encodeURIComponent(containerNumber)}`, data);
export const createArnulfoPayment = (data) => api.post('/arnulfo/payment', data);
export const deleteArnulfoPayment = (paymentId) => api.delete(`/arnulfo/payment/${encodeURIComponent(paymentId)}`);

// Admin - Migration
export const migrateStatusCatalogs = () => api.post('/migrate-statuses');

// Alerts
export const getAlertTypes = () => api.get('/alerts/types');
export const getAlertConfigs = () => api.get('/alerts/configs');
export const updateAlertConfig = (alertType, data) => api.put(`/alerts/configs/${alertType}`, data);
export const getAlertLogs = (params = {}) => api.get('/alerts/logs', { params });
export const testAlert = (alertType) => api.post(`/alerts/test/${alertType}`);
export const checkAlertsNow = () => api.post('/alerts/check-now');

// Pedimentos
export const listPedimentos = (params = {}) => api.get('/pedimentos', { params });
export const uploadPedimento = (formData, params) => api.post('/pedimentos/upload' + '?' + new URLSearchParams(params).toString(), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const extractPedimentoOnly = (formData, params) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qs.append(k, v); });
  return api.post('/pedimentos/extract-only?' + qs.toString(), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadPedimentosBulk = (files, { visibleToClient = true } = {}) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const qs = new URLSearchParams({ visible_to_client: String(visibleToClient) });
  return api.post('/pedimentos/upload-bulk?' + qs.toString(), fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const consultSoiaForShipment = (mbl) => api.post('/pedimentos/soia/consult/' + encodeURIComponent(mbl));
export const backfillArrivalDates = () => api.post('/analytics/backfill-arrival-dates');
export const extractPedimentosOnlyBulk = (files) => {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return api.post('/pedimentos/extract-only-bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadPedimento = (pedimentoId) => api.get(`/pedimentos/${pedimentoId}/download`, { responseType: 'blob' });
export const soiaLookup = (data) => api.post('/documents/soia-parse-pedimento', data);
export const updatePedimento = (pedimentoId, data) => api.put(`/pedimentos/${pedimentoId}`, data);
export const deletePedimento = (pedimentoId) => api.delete(`/pedimentos/${pedimentoId}`);

// Certificates
export const listCertificates = (params = {}) => api.get('/certificates', { params });
export const getCertificatesSummary = () => api.get('/certificates/summary');
export const createCertificate = (data) => api.post('/certificates', data);
export const updateCertificate = (certId, data) => api.put(`/certificates/${certId}`, data);
export const deleteCertificate = (certId) => api.delete(`/certificates/${certId}`);
export const uploadCertificateFile = (certId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/certificates/${certId}/upload-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const getCertificatesForShipment = (mbl) => api.get(`/certificates/for-shipment/${mbl}`);
export const getSuperAdminAudit = (params = {}) => api.get('/audit/super-admin', { params });
export const getSuperAdminAuditActions = () => api.get('/audit/super-admin/actions');
export const importCertificatesJson = (certificates, overwrite_existing_ids = false) => api.post('/certificates/import-json', { certificates, overwrite_existing_ids });

// Pricing
export const listPrices = (params = {}) => api.get('/pricing', { params });
export const getProductNames = () => api.get('/pricing/products');
export const getPriceHistory = (productName, params = {}) => api.get(`/pricing/history/${encodeURIComponent(productName)}`, { params });
export const createPriceEntry = (data) => api.post('/pricing', data);
export const updatePriceEntry = (entryId, data) => api.put(`/pricing/${entryId}`, data);
export const deletePriceEntry = (entryId) => api.delete(`/pricing/${entryId}`);
export const lookupPrice = (product, supplier) => api.get('/pricing/lookup', { params: { product, supplier } });
export const bulkImportPrices = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/pricing/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const generateInvoicePL = async (mbl, body) => {
  const res = await api.post(`/documents/generate-invoice-pl/${encodeURIComponent(mbl)}`, body, { responseType: 'blob' });
  return res;
};

export const generateCartaExplicacion = async (mbl, body) => {
  const res = await api.post(`/documents/generate-carta-explicacion/${encodeURIComponent(mbl)}`, body, { responseType: 'blob' });
  return res;
};

export const generateContratoCompraventa = async (mbl, body) => {
  const res = await api.post(`/documents/generate-contrato/${encodeURIComponent(mbl)}`, body, { responseType: 'blob' });
  return res;
};


// Documents
export const getDocumentTypes = () => api.get('/documents/types');
export const getShipmentDocuments = (mbl, params = {}) => api.get(`/documents/shipment/${encodeURIComponent(mbl)}`, { params });
export const uploadDocument = (formData, params) => api.post('/documents/upload?' + new URLSearchParams(params).toString(), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const downloadDocument = (docId) => api.get(`/documents/${docId}/download`, { responseType: 'blob' });
export const updateDocument = (docId, data) => api.put(`/documents/${docId}`, data);
export const deleteDocument = (docId) => api.delete(`/documents/${docId}`);

// Tracking
export const getActiveContainersTracking = (params = {}) => api.get('/tracking/active-containers', { params });
export const scrapeContainerTracking = (containerNumber, force = false) => api.post(`/tracking/scrape/${containerNumber}?force=${force}`);
export const getContainerTrackingData = (containerNumber) => api.get(`/tracking/data/${containerNumber}`);

// Port Terminal Tracking
export const getPortTerminals = () => api.get('/tracking/port-terminals');
export const scrapePortTracking = (containerNumber, terminalCode) => api.post(`/tracking/port/${containerNumber}/${terminalCode}`);
export const scrapePortByBL = (blNumber, terminalCode) => api.post(`/tracking/port-bl/${blNumber}/${terminalCode}`);

// Email Settings
export const getEmailConfig = () => api.get('/email-settings/config');
export const saveEmailConfig = (data) => api.post('/email-settings/config', data);
export const testEmailConnection = () => api.post('/email-settings/test-connection');
export const getDiagnosticsEmailConfig = () => api.get('/diagnostics/email-config');
export const sendDiagnosticsTestEmail = (payload) => api.post('/diagnostics/send-test-email', payload);
export const getFerrovalleCredentials = () => api.get('/diagnostics/ferrovalle/credentials');
export const revealFerrovallePassword = () => api.get('/diagnostics/ferrovalle/credentials/reveal');
export const setFerrovalleCredentials = (payload) => api.post('/diagnostics/ferrovalle/credentials', payload);
export const testFerrovalleLogin = () => api.post('/diagnostics/ferrovalle/test-login');
export const resetFerrovalleLockout = () => api.post('/diagnostics/ferrovalle/reset-lockout');
export const checkInboxNow = () => api.post('/email-settings/check-inbox');
export const getParsedEmails = (params = {}) => api.get('/email-settings/parsed-emails', { params });
export const applyEmailUpdate = (emailId) => api.post(`/email-settings/parsed-emails/${emailId}/apply`);
export const dismissEmail = (emailId) => api.post(`/email-settings/parsed-emails/${emailId}/dismiss`);

// Reports
export const previewMonthlyReport = (params = {}) => api.get('/reports/monthly/preview', { params });
export const sendMonthlyReport = (params = {}) => api.post('/reports/monthly/generate', null, { params });

// SaaS Dashboard (Super Admin)
export const getSaasDashboard = () => api.get('/saas-dashboard');
export const exportShipmentsFull = (includeDelivered = false) =>
  api.get(`/shipments/export-full?include_delivered=${includeDelivered}`);
export const importShipmentsFull = (bundle, overwrite = false) =>
  api.post(`/shipments/import-full?overwrite=${overwrite}`, bundle);
export const inviteUser = (orgId, data) => api.post(`/organizations/${orgId}/invite`, data);
export const getOrgUsers = (orgId) => api.get(`/organizations/${orgId}/users`);
export const removeOrgUser = (orgId, userId) => api.delete(`/organizations/${orgId}/users/${userId}`);
export const getMyFeatures = () => api.get('/my-features');
export const runIsimartMigration = () => api.post('/migrate/setup-isimart');
export const impersonateUser = (userId) => api.post(`/impersonate/${userId}`);

// Notifications API
export const getNotifications = ({ limit = 50, onlyUnread = false } = {}) =>
  api.get(`/notifications?limit=${limit}${onlyUnread ? '&only_unread=true' : ''}`);
export const markNotificationsRead = (ids) =>
  api.post('/notifications/mark-read', ids ? { ids } : {});
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// AI Arancel API
export const getAIAranSettings = () => api.get('/ai/settings');
export const saveAIAranSettings = (data) => api.post('/ai/settings', data);
export const deleteAIAranSettings = () => api.delete('/ai/settings');
export const getAIAranTemplates = () => api.get('/ai/templates');
export const sendAIAranChat = (data) => api.post('/ai/chat', data);
export const listAIAranSessions = () => api.get('/ai/sessions');
export const getAIAranSession = (sessionId) => api.get(`/ai/sessions/${sessionId}`);
export const deleteAIAranSession = (sessionId) => api.delete(`/ai/sessions/${sessionId}`);

// Public Tracking Link (auth-protected management endpoints)
export const getPublicTrackingLink = (mbl) =>
  api.get(`/shipments/${encodeURIComponent(mbl)}/public-link`);
export const generatePublicTrackingLink = (mbl) =>
  api.post(`/shipments/${encodeURIComponent(mbl)}/public-link`);
export const revokePublicTrackingLink = (mbl) =>
  api.delete(`/shipments/${encodeURIComponent(mbl)}/public-link`);
// Public viewer (no auth) — used by PublicTrackingPage
export const fetchPublicTracking = (token) =>
  axios.get(`${API_URL}/public/track/${encodeURIComponent(token)}`);

export default api;
